import { ORDER_STATUS, PAYMENT_METHOD } from '@/utils/constants'
import { generateOrderId } from '@/utils/formatters'

// Export the mock orders from products.js
export { mockOrders } from './products.js'

// Additional mock data for dashboard stats
export const mockOrderStats = {
  totalSales: 125430.50,
  totalOrders: 1024,
  pendingOrders: 45,
  canceledOrders: 12,
  recentActivity: [
    {
      id: '1',
      type: 'order_created',
      message: 'New order #ORD-1024 created',
      timestamp: '2024-01-23T10:30:00Z',
    },
    {
      id: '2',
      type: 'order_delivered',
      message: 'Order #ORD-1020 delivered',
      timestamp: '2024-01-23T09:15:00Z',
    },
    {
      id: '3',
      type: 'order_canceled',
      message: 'Order #ORD-1019 canceled',
      timestamp: '2024-01-23T08:45:00Z',
    },
  ],
  salesByState: [
    { state: 'Maharashtra', flag: '😄', sales: 45230.50, percentage: 36 },
    { state: 'Delhi', flag: '😊', sales: 25180.30, percentage: 20 },
    { state: 'Karnataka', flag: '🙂', sales: 18950.20, percentage: 15 },
    { state: 'Tamil Nadu', flag: '🤔', sales: 15420.80, percentage: 12 },
    { state: 'Uttar Pradesh', flag: '😐', sales: 12380.70, percentage: 10 },
    { state: 'Others', flag: '👋', sales: 8268.00, percentage: 7 },
  ],
}

// API Functions
export async function getOrders(filters = {}) {
  await new Promise(resolve => setTimeout(resolve, 500))
  
  // Import from products.js to get the latest data
  const { mockOrders } = await import('./products.js')
  let filtered = [...mockOrders]
  
  // Apply filters
  if (filters.search) {
    const search = filters.search.toLowerCase()
    filtered = filtered.filter(order => 
      order.orderNumber.toLowerCase().includes(search) ||
      order.customerName.toLowerCase().includes(search) ||
      order.customerEmail.toLowerCase().includes(search)
    )
  }
  
  if (filters.status) {
    filtered = filtered.filter(order => order.status === filters.status)
  }
  
  if (filters.payment) {
    filtered = filtered.filter(order => order.paymentMethod === filters.payment)
  }
  
  // Date range filtering (filters.from and filters.to are expected as YYYY-MM-DD or ISO strings)
  if (filters.from || filters.to) {
    const from = filters.from ? new Date(filters.from) : null
    const to = filters.to ? new Date(filters.to) : null
    if (to) to.setHours(23,59,59,999)
    filtered = filtered.filter(order => {
      const d = new Date(order.createdAt)
      if (from && to) return d >= from && d <= to
      if (from) return d >= from
      if (to) return d <= to
      return true
    })
  }
  
  // Apply sorting
  if (filters.sort) {
    filtered.sort((a, b) => {
      const order = filters.order === 'desc' ? -1 : 1
      
      switch (filters.sort) {
        case 'date':
          return order * (new Date(a.createdAt) - new Date(b.createdAt))
        case 'total':
          return order * (a.total - b.total)
        case 'customer':
          return order * a.customerName.localeCompare(b.customerName)
        default:
          return 0
      }
    })
  }
  
  // Apply pagination
  const page = filters.page || 1
  const size = filters.size || 10
  const start = (page - 1) * size
  const end = start + size
  
  return {
    data: filtered.slice(start, end),
    meta: {
      page,
      size,
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / size),
    },
  }
}

export async function getOrder(id) {
  await new Promise(resolve => setTimeout(resolve, 300))
  
  const { mockOrders } = await import('./products.js')
  const order = mockOrders.find(o => o.id === id)
  if (!order) {
    throw new Error('Order not found')
  }
  
  return order
}

export async function updateOrderStatus(id, status) {
  await new Promise(resolve => setTimeout(resolve, 500))
  
  const { mockOrders } = await import('./products.js')
  const index = mockOrders.findIndex(o => o.id === id)
  if (index === -1) {
    throw new Error('Order not found')
  }
  
  mockOrders[index] = {
    ...mockOrders[index],
    status,
    updatedAt: new Date().toISOString(),
  }
  
  return mockOrders[index]
}

export async function deleteOrder(id) {
  await new Promise(resolve => setTimeout(resolve, 300))

  const { mockOrders } = await import('./products.js')
  const index = mockOrders.findIndex(o => o.id === id)
  if (index === -1) {
    throw new Error('Order not found')
  }

  mockOrders.splice(index, 1)
  return { success: true }
}

export async function createOrder(orderData) {
  await new Promise(resolve => setTimeout(resolve, 400))

  const { mockOrders } = await import('./products.js')
  const newOrder = {
    id: generateOrderId(),
    orderNumber: `ORD-${String(mockOrders.length + 1).padStart(3, '0')}`,
    customerId: orderData.customerId || `CUST-${String(mockOrders.length + 1).padStart(3, '0')}`,
    customerName: orderData.customerName || 'New Customer',
    customerEmail: orderData.customerEmail || '',
    customerPhone: orderData.customerPhone || '',
    items: orderData.items || [],
    subtotal: orderData.subtotal || 0,
    tax: orderData.tax || 0,
    shipping: orderData.shipping || 0,
    total: orderData.total || 0,
    status: orderData.status || ORDER_STATUS.PENDING,
    paymentMethod: orderData.paymentMethod || PAYMENT_METHOD.CREDIT_CARD,
    paymentStatus: orderData.paymentStatus || 'pending',
    shippingAddress: orderData.shippingAddress || {},
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  mockOrders.unshift(newOrder)
  return newOrder
}

export async function getOrderStats() {
  await new Promise(resolve => setTimeout(resolve, 300))
  return mockOrderStats
}